import './base-input.scss';

import { kebabCase } from 'lodash';
import React, {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  MouseEvent,
  useImperativeHandle,
  useRef,
} from 'react';

export enum BaseInputSize {
  DEFAULT = 'DEFAULT',
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
}

export enum BaseInputIcon {
  NONE = 'NONE',
  SEARCH = 'SEARCH',
  SELECT = 'SELECT',
  DATE = 'DATE',
  FILE = 'FILE',
  EYE = 'EYE',
  EYE_OFF = 'EYE-OFF',
  CLOSE = 'CLOSE',
}

export type BaseInputProps = {
  testId?: string;
  type?: string;
  size?: BaseInputSize;
  disabled?: boolean;
  name: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  maxLength?: number;
  pattern?: string;
  min?: string | number;
  max?: string | number;
  step?: number;
  autocomplete?: string | undefined;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
  onChange?: (value: string, e: ChangeEvent<HTMLElement>) => void;
  onBlur?: (value: string, e: FocusEvent<HTMLElement>) => void;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
};

// eslint-disable-next-line react/display-name
export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      testId,
      type = 'text',
      size = BaseInputSize.DEFAULT,
      disabled = false,
      name,
      placeholder = '',
      onChange,
      onBlur,
      onPaste,
      ...props
    }: BaseInputProps,
    outerRef
  ) => {
    const ref = useRef<HTMLInputElement>(null);

    useImperativeHandle(outerRef, () => ref.current!, []);

    return (
      <input
        {...props}
        data-test-id={testId}
        id={name}
        type={type}
        name={name}
        disabled={disabled}
        className={`form-control${
          size !== BaseInputSize.DEFAULT
            ? ` form-control--${kebabCase(size)}`
            : ``
        }`}
        placeholder={placeholder}
        autoComplete={props.autocomplete}
        onChange={e => onChange?.(e.currentTarget.value, e)}
        onBlur={e => onBlur?.(e.currentTarget.value, e)}
        onPaste={e => onPaste?.(e)}
        ref={ref}
      />
    );
  }
);
